'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

type ForgotForm = { email: string };

export default function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const supabase = createClient();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ForgotForm>();

  const onSubmit = async (data: ForgotForm) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${(process.env.NEXT_PUBLIC_SITE_URL ?? '').replace(/\/+$/, '')}/reset-password`,
      });
      if (error) throw error;
      setSent(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reset email');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <AppLogo src="/assets/images/opclogo-1784542402159.png" size={40} />
          <span className="font-bold text-lg text-foreground">Operator Circle</span>
        </div>

        <div className="card-elevated p-8">
          {sent ? (
            <div className="text-center">
              <div className="w-14 h-14 bg-success-bg rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={28} className="text-success" />
              </div>
              <h1 className="text-xl font-bold text-foreground mb-2">Check Your Email</h1>
              <p className="text-muted-foreground text-sm mb-6">
                If an account exists for that email address, you will receive a password reset link shortly.
              </p>
              <Link href="/sign-in-login-screen" className="btn-primary w-full py-3 flex items-center justify-center gap-2">
                <ArrowLeft size={16} />
                Return to Sign In
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-info-bg rounded-xl flex items-center justify-center">
                  <Mail size={18} className="text-info" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-foreground">Forgot Password</h1>
                  <p className="text-muted-foreground text-xs mt-0.5">Enter your email to receive a reset link</p>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                  <input
                    type="email"
                    {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } })}
                    placeholder="your@email.com"
                    className="input-field"
                  />
                  {errors.email && <p className="text-danger text-xs mt-1">{errors.email.message}</p>}
                </div>

                <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-3.5">
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </span>
                  ) : 'Send Reset Link'}
                </button>
              </form>

              <div className="mt-5 text-center">
                <Link href="/sign-in-login-screen" className="text-accent text-sm hover:underline flex items-center justify-center gap-1">
                  <ArrowLeft size={14} />
                  Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
